import * as THREE from 'three';

import { COLORS } from '../game/constants';
import { samplePixels, type PixelSample } from '../utils/debug';

/** Paint a soft radial blob, used to fake neon light sources in the env map. */
function addBlob(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  color: string,
): void {
  const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
  gradient.addColorStop(0, color);
  gradient.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(cx - radius, cy - radius, radius * 2, radius * 2);
}

/**
 * A small equirectangular image used as the scene environment.
 *
 * Without an environment map, `metalness: 1` surfaces (the ball, the chrome
 * trim) render as near-black. Generating one procedurally avoids shipping an HDR
 * file in Phase 1 while still giving the metal something to reflect.
 */
function createEnvironmentTexture(): THREE.CanvasTexture {
  const width = 256;
  const height = 128;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (ctx) {
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, '#8fc4ff');
    gradient.addColorStop(0.45, '#33507f');
    gradient.addColorStop(1, '#0b1226');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    addBlob(ctx, width * 0.22, height * 0.34, 52, 'rgba(120,235,255,1)');
    addBlob(ctx, width * 0.74, height * 0.44, 46, 'rgba(255,120,210,0.95)');
    addBlob(ctx, width * 0.50, height * 0.12, 40, 'rgba(255,255,255,1)');
    addBlob(ctx, width * 0.92, height * 0.62, 36, 'rgba(150,225,255,0.9)');
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.mapping = THREE.EquirectangularReflectionMapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/** The dark arcade hall behind the machine. */
function createBackdrop(): THREE.Mesh {
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d');
  if (ctx) {
    const gradient = ctx.createRadialGradient(
      size / 2,
      size / 2,
      size * 0.04,
      size / 2,
      size / 2,
      size * 0.6,
    );
    gradient.addColorStop(0, '#12224a');
    gradient.addColorStop(0.55, '#0a1430');
    gradient.addColorStop(1, '#03060f');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;

  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(30, 30),
    new THREE.MeshBasicMaterial({ map: texture, depthWrite: false, toneMapped: false }),
  );
  mesh.position.set(0, 0, -3);
  mesh.name = 'backdrop';
  return mesh;
}

/**
 * Owns the renderer, the scene graph root, and the resize contract.
 *
 * Deliberately knows nothing about gameplay - it just renders whatever the game
 * layer has put into the scene.
 */
export class SceneManager {
  readonly scene: THREE.Scene;
  readonly renderer: THREE.WebGLRenderer;
  readonly canvas: HTMLCanvasElement;

  private readonly pmrem: THREE.PMREMGenerator;
  private readonly environmentSource: THREE.CanvasTexture;
  private readonly environmentTarget: THREE.Texture;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.25;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(COLORS.background);

    this.pmrem = new THREE.PMREMGenerator(this.renderer);
    this.pmrem.compileEquirectangularShader();

    this.environmentSource = createEnvironmentTexture();
    this.environmentTarget = this.pmrem.fromEquirectangular(this.environmentSource).texture;
    this.scene.environment = this.environmentTarget;
    this.scene.environmentIntensity = 1.15;

    this.scene.add(createBackdrop());
  }

  /** Resize the drawing buffer. The canvas is sized by CSS, so styles are left alone. */
  resize(width: number, height: number): void {
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(width, height, false);
  }

  render(camera: THREE.Camera): void {
    this.renderer.render(this.scene, camera);
  }

  /**
   * Read the framebuffer immediately after a render.
   *
   * Must be called inside the same frame as `render()`, otherwise the browser
   * may already have cleared or swapped the drawing buffer.
   */
  readPixelSample(): PixelSample {
    const gl = this.renderer.getContext();
    const { width, height } = this.renderer.domElement;
    return samplePixels(gl, width, height);
  }

  dispose(): void {
    this.environmentTarget.dispose();
    this.environmentSource.dispose();
    this.pmrem.dispose();
    this.renderer.dispose();
  }
}
